import { captureException } from "@sentry/react";
import cryptoes from "crypto-es";
import { LibraryMap } from "../typings/backend/library";
import { BackendConfig, GetMemberResponse } from "../typings/backend/index";
import { storageKeys } from "@src/constants";
// import { getPushSub } from "@src/utils";
// import { unregisterPusNotifications } from "./backend";

const AES = cryptoes.AES;
const enc = cryptoes.enc;

const API_URL = import.meta.env.VITE_API_URL;
const APP_VERSION = import.meta.env.VITE_VERSION;
const AUTHINFO_MAXAGE_ms = 600 * 1000;
// const AUTHINFO_MAXAGE_NOTMEMBER_ms = 300*1000;

const secrets = {
  appDataStorageKey: "adsk1",
  appDataStorageValue: "adsv1",
};
const TOKEN_HEADER_KEY = ["x", "-", "w", "e", "ep", "y", "-", "to", "k", "en"].join("");
const APP_VERSION_HEADER_KEY = [
  "x",
  "-",
  "w",
  "e",
  "e",
  "p",
  "y",
  "-",
  "w",
  "e",
  "ba",
  "p",
  "p",
  "-",
  "v",
  "er",
  "si",
  "on",
].join("");
const AUTHINFO_REFRESHED_KEY = ["r", "e", "f", "re", "sh", "e", "d"].join("");

export const defaultNotAuthed = {
  authed: false,
  email: "",
  name: "",
  is_member: false,
  is_manager: false,
  picture: "",
  token: "",
  roles: [],
};

/** Returns the last set value, should be a Date.now() from the past or undefined. */
const getAuthInfoRefreshDate = () => {
  const value = localStorage.getItem(AUTHINFO_REFRESHED_KEY);
  if (value) return +value;
};
const setAuthInfoRefreshDate = (val = Date.now()) => localStorage.setItem(AUTHINFO_REFRESHED_KEY, String(val));
export const getToken = () => getAppData().authInfo?.token;
const getStoreKey = () => ["a", "l"].join("_") + "e" + ["k"].join("");
const removeAppDataKey = () => localStorage.removeItem(getStoreKey());
export const storeAppData = (data: AppData) => {
  const encrypted = AES.encrypt(JSON.stringify(data), secrets.appDataStorageValue).toString();
  localStorage.setItem(getStoreKey(), encrypted);
};
export const getAppData = (): AppData => {
  try {
    let data = "{}";
    const encrypted = localStorage.getItem(getStoreKey());
    if (encrypted) {
      const bytes = AES.decrypt(encrypted, secrets.appDataStorageValue);
      data = bytes.toString(enc.Utf8);
    }
    return JSON.parse(data);
  } catch (error) {
    captureException(error);
    removeAppDataKey();
    alert(`A fatal error occured, please try signing in again.`);
    window.location.pathname = "/login";
    throw error;
  }
};

/**
 * AuthInfo that automatically refreshes if expired.
 */
export const isAuthed = async (refresh?: boolean): Promise<AuthInfo> => {
  const cachedAuthInfo = getCachedAuthInfo();
  const LAST_AUTHINFO_REFRESH = getAuthInfoRefreshDate();
  if (
    cachedAuthInfo.authed &&
    (refresh || !LAST_AUTHINFO_REFRESH || Date.now() - LAST_AUTHINFO_REFRESH > AUTHINFO_MAXAGE_ms)
  ) {
    const member = await getMember();
    const refreshedAuthInfo: AuthInfo = {
      authed: true,
      name: member.name,
      token: cachedAuthInfo.token,
      email: member.email,
      is_member: member.is_member,
      is_manager: member.is_manager || false,
      picture: member.picture,
      roles: (member.roles as any) || [],
    };
    // const refreshedAuthInfo: AuthInfo = Object.assign({}, cachedAuthInfo, { ...member });
    storeAppData({
      ...getAppData(),
      authInfo: refreshedAuthInfo,
      config: member.config,
      data_config: member.data_config,
    });
    setAuthInfoRefreshDate();
    return getAppData().authInfo as AuthInfo;
  }
  return cachedAuthInfo;
};

export const getCachedAuthInfo = () => {
  return getAppData().authInfo || defaultNotAuthed;
};

const makeReq = async (method: string, path: string, jsonBody?: Object) => {
  if (!path.startsWith("/")) path = `/${path}`;
  const url = encodeURI(`${API_URL}${path}`);
  const headers: { [key: string]: string } = {
    [TOKEN_HEADER_KEY]: getToken() || "",
    [APP_VERSION_HEADER_KEY]: APP_VERSION || "",
  };
  if (jsonBody) headers["content-type"] = "application/json";
  const res = await fetch(url, {
    method,
    headers,
    body: jsonBody ? JSON.stringify(jsonBody) : undefined,
  });
  let body = null;
  try {
    body = await res.json();
  } catch (err) {
    /* */
  }
  await checkResponse(res, body);
  return {
    status: res.status,
    body,
    _res: res,
  };
};

export const loginToBackend = async (googleUser: any, redirect_path?: string) => {
  const url = encodeURI(`${API_URL}/api/auth/g-login`);
  const res = await fetch(url, {
    method: "POST",
    headers: {
      "content-type": "application/json",
    },
    body: JSON.stringify(googleUser),
  });
  if (res.status === 200) {
    const body: HandleLoginResponseSuccess = await res.json();
    const authInfo: AuthInfo = {
      authed: true,
      name: body.name,
      token: body.token,
      email: body.email,
      is_member: body.is_member,
      is_manager: body.is_manager || false,
      picture: body.picture,
    };
    storeAppData({ ...getAppData(), authInfo });
    window.location.href = redirect_path || "/";
    // window.location.hash = "";
    // window.location.pathname = "/";
  } else {
    throw new Error((await res.json()).message || res.statusText);
  }
};

export const logout = async () => {
  try {
    const res = (await makeReq("POST", "/api/auth/logout"))._res;
    // if (getPushSub()) {
    //   unregisterPusNotifications().catch(console.warn);
    // }
    let body = null;
    try {
      body = (await res.json()).body as { message?: string };
    } catch (err) {
      /* */
    }
    if (res.status === 200) {
      storeAppData({ authInfo: defaultNotAuthed });
      localStorage.removeItem(AUTHINFO_REFRESHED_KEY);
      localStorage.removeItem(storageKeys.quickSearchHistory);
      window.location.href = "/";
    } else if (res.status === 503) {
      alert(body?.message || "The server is currently unable to handle this request, please try again later.");
    } else {
      alert(
        body?.message || `Sorry, there was an unexpected response (${res.status} ${res.statusText}), please try again later.`
      );
    }
  } catch (error) {
    console.error(error);
    alert(`Sorry, there was an unexpected error: ${(error as Error)?.message}`);
  }
};

/* REQUESTS THAT REQUIRE AUTH */

/**
 * Helper for API responses.
 * If the resposne has .must_revalidate, set the default auth info and redirect to /login.
 */
const checkResponse = async (res: Response, body: any) => {
  try {
    if (body?.must_revalidate) {
      storeAppData({ authInfo: defaultNotAuthed });
      throw new Error("must_revalidate");
    }
    // else if (res.status === 401 && body.message?.includes('E002')) {
    //
    // }
  } catch (error) {
    if ((error as Error).message.toLowerCase().includes("must_revalidate")) window.location.pathname = "/login";
    throw error;
  }
};

export const getMember = async () => {
  const data: GetMemberResponse = (await makeReq("GET", "/api/member/me")).body; // GetMemberResponse assumes success
  return data;
  // return (await apiReq.get('/api/member/me')).data as GetMemberResponse;
};

type SyncResponse = { id: number; folder_id: string; folder_name: string; http_path_name: string; maps: LibraryMap[] };
type AsyncResponse = { message: string };
export async function scanLib(http_path_name: string, mode: "sync"): Promise<{ status: number; body: SyncResponse }>;
export async function scanLib(http_path_name: string, mode: "async"): Promise<{ status: number; body: AsyncResponse }>;
export async function scanLib(
  http_path_name: string,
  mode: "sync" | "async"
): Promise<{ status: number; body: SyncResponse | AsyncResponse }> {
  const res = await makeReq("POST", `/api/soda/scan/${http_path_name}?mode=${mode}`);
  const body = res.body;
  if (!body.id && !body.folder_id && !body.message)
    throw new Error(`Unexpected response: ${res._res.status} (${res._res.statusText})`);
  if (res.status >= 400)
    throw new Error(body.message || `Unexpected status code: ${res._res.status} (${res._res.statusText})`);
  return {
    status: res._res.status,
    body,
  };
}

/* TYPES */
export interface AppData {
  authInfo?: AuthInfo;
  config?: BackendConfig;
  data_config?: import("../typings/backend/index").GetMemberResponse["data_config"];
  // authInfo_refreshed_at?: number;
}
export interface AuthInfo {
  /** true if user is logged in (including non-members) */
  authed: boolean;
  email: string;
  name: string | null;
  is_manager: boolean;
  token: string | null;
  is_member: boolean;
  picture: string | null | undefined;
  role?: string;
  roles?: ("admin" | "tester" | "editor" | "translator" | "tier_1" | "tier_2" | "tier_3")[];
}

interface HandleLoginResponseSuccess {
  token: string;
  email: string;
  name: string | null;
  is_member: boolean;
  picture: string | null | undefined;
  role?: string;
  is_manager?: boolean;
}
